import { useState } from "react";
import {
	Menu,
	Mic,
	Volume2,
	Film,
	HelpCircle,
	ChevronRight,
	X,
} from "lucide-react";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from "@/presentation/components/ui/dialog";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/presentation/components/ui/sheet";
import { VoiceSettingsContent } from "@/presentation/components/VoiceSettingsContent";
import { VideoSettingsContent } from "@/presentation/components/VideoSettingsContent";
import { UsageGuideContent } from "@/presentation/components/UsageGuideContent";
import { VoiceSelector } from "@/presentation/components/VoiceSelector";

type ActiveModal = "voice-select" | "voice" | "video" | "usage" | null;

interface MenuItem {
	label: string;
	description: string;
	icon: typeof Volume2;
	iconBg: string;
	iconColor: string;
	modal: Exclude<ActiveModal, null>;
}

const MENU_ITEMS: MenuItem[] = [
	{
		label: "Voice選択",
		description: "ElevenLabs Voice一覧から選択",
		icon: Mic,
		iconBg: "bg-amber-100",
		iconColor: "text-amber-600",
		modal: "voice-select",
	},
	{
		label: "API設定",
		description: "API Key・声の調整",
		icon: Volume2,
		iconBg: "bg-coral-100",
		iconColor: "text-coral-600",
		modal: "voice",
	},
	{
		label: "動画設定",
		description: "FPS・トランジション・表示時間・音量",
		icon: Film,
		iconBg: "bg-teal-100",
		iconColor: "text-teal-600",
		modal: "video",
	},
	{
		label: "使い方ガイド",
		description: "アプリの使い方を確認",
		icon: HelpCircle,
		iconBg: "bg-purple-100",
		iconColor: "text-purple-600",
		modal: "usage",
	},
];

export function HamburgerMenu() {
	const [sheetOpen, setSheetOpen] = useState(false);
	const [activeModal, setActiveModal] = useState<ActiveModal>(null);
	// クローズアニメーション中もタイトルとコンテンツを描画し続けるため、最後に開いたモーダルを保持する
	const [lastModal, setLastModal] = useState<Exclude<ActiveModal, null> | null>(null);

	const openModal = (modal: Exclude<ActiveModal, null>) => {
		setActiveModal(modal);
		setLastModal(modal);
	};

	const displayModal = activeModal ?? lastModal;
	const activeItem = displayModal
		? MENU_ITEMS.find((item) => item.modal === displayModal)
		: undefined;

	return (
		<>
			<Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
				<SheetTrigger asChild>
					<button className="btn-icon" aria-label="メニュー">
						<Menu size={20} />
					</button>
				</SheetTrigger>
				<SheetContent
					side="left"
					className="flex flex-col overflow-hidden p-0 w-3/4 max-w-md"
				>
					<SheetHeader className="px-0 py-0">
						<div className="flex items-center gap-3 px-6 py-4 border-b border-warm-100">
							<SheetTitle>メニュー</SheetTitle>
						</div>
						<SheetDescription className="sr-only">
							設定と使い方ガイドのメニュー
						</SheetDescription>
					</SheetHeader>

					<div className="flex-1 overflow-y-auto">
						<nav className="flex flex-col gap-1 p-4">
							{MENU_ITEMS.map((item) => (
								<button
									key={item.modal}
									onClick={() => openModal(item.modal)}
									className="flex items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-warm-100"
								>
									<div
										className={`w-9 h-9 rounded-lg ${item.iconBg} flex items-center justify-center shrink-0`}
									>
										<item.icon
											size={18}
											className={item.iconColor}
										/>
									</div>
									<div className="flex-1 min-w-0">
										<span className="block text-sm font-medium text-warm-800">
											{item.label}
										</span>
										<span className="block text-xs text-warm-400">
											{item.description}
										</span>
									</div>
									<ChevronRight
										size={16}
										className="text-warm-300 shrink-0"
									/>
								</button>
							))}
						</nav>
					</div>
				</SheetContent>
			</Sheet>

			{/* Modals */}
			<Dialog
				open={activeModal !== null}
				onOpenChange={(isOpen) => {
					if (!isOpen) setActiveModal(null);
				}}
			>
				<DialogContent
					className="z-60 w-[calc(100vw-2rem)] max-w-[40rem] p-0 gap-0 overflow-hidden"
					showCloseButton={false}
				>
					{activeItem && (
						<>
							<div className="modal-header">
								<div className="flex items-center gap-2">
									<activeItem.icon
										size={18}
										className={activeItem.iconColor}
									/>
									<DialogTitle asChild>
										<h2>{activeItem.label}</h2>
									</DialogTitle>
								</div>
								<DialogClose asChild>
									<button
										className="btn-icon"
										aria-label="閉じる"
									>
										<X size={18} />
									</button>
								</DialogClose>
							</div>
							<DialogDescription className="sr-only">
								{activeItem.label}
							</DialogDescription>
							<div className="modal-body">
								{displayModal === "voice-select" && (
									<VoiceSelector />
								)}
								{displayModal === "voice" && (
									<VoiceSettingsContent />
								)}
								{displayModal === "video" && (
									<VideoSettingsContent />
								)}
								{displayModal === "usage" && (
									<UsageGuideContent />
								)}
							</div>
						</>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}
